/**
 * MyProfile — strona profilu zalogowanego usera tenantowego (F7b T7b.4 + F10 T10.11).
 *
 * Karty:
 *   1. „Dane konta" — email, displayName, isSuperUser badge.
 *   2. „Profil i jezyk" — edytowalne displayName + select PL/EN. (T10.11)
 *   3. „Avatar" — upload (multipart) + remove. UI: kolko 96x96, podglad lokalny
 *      po wyborze pliku (FileReader -> data URL); po wejsciu na strone zapisany avatar
 *      wczytywany z GET /api/auth/profile/avatar (blob przez Bearer). (T10.11)
 *   4. „Bezpieczenstwo" — 2FA status badge + CTA (Setup) + ChangePasswordForm.
 *   5. „Sesje" — button „Wyloguj ze wszystkich sesji" -> confirm modal -> logoutAll.
 *
 * Profil/jezyk save:
 *   - PUT /api/auth/profile {displayName?, language?}.
 *   - Po sukcesie: i18n.changeLanguage(newLang) + refreshMe (pewnosc).
 *   - 422 VALIDATION_FAILED -> toast „nieprawidlowe dane".
 *
 * Avatar:
 *   - POST /api/auth/profile/avatar (multipart) — pole `file`.
 *   - Walidacje frontowe: max 5MB, MIME jpeg/png/webp. Backend wymusza twardo.
 *   - DELETE /api/auth/profile/avatar — czysci avatarPath + usuwa z MinIO.
 *   - Stan lokalny `previewUrl` (data URL po FileReader) zywy po uploadzie az do reload.
 */

import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { RECORD_LANGUAGES, type RecordLanguage } from '@saas/shared';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ChangePasswordForm } from '@/components/ChangePasswordForm';
import { Disable2FAModal } from '@/components/Disable2FAModal';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import i18n from '@/i18n';
import { api } from '@/lib/api';

const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const AVATAR_ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);

// `embedded` = renderowany wewnątrz panelu Ustawień (bez własnego <main>/tła/min-h-screen),
// żeby „Profil" w Ustawieniach pokazywał treść OD RAZU, bez pośredniej karty-linku.
// Komponent MUSI żyć poza `MyProfile`: zdefiniowany w renderze dostawałby nową tożsamość
// przy każdym renderze, React przemontowywałby całe drzewo i pole traciło fokus po 1 znaku.
function ProfileShell({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  return embedded ? (
    <>{children}</>
  ) : (
    <main className="min-h-screen bg-bg-secondary p-6">{children}</main>
  );
}

export function MyProfile({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const { me, loading, logoutAll, refreshMe } = useTenantAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  // Profil/jezyk edit state.
  const loadedDisplayName = me?.user.displayName ?? '';
  const [profileDisplayName, setProfileDisplayName] = useState<string>(loadedDisplayName);
  // Po twardym przeładowaniu `me` dojeżdża PO pierwszym renderze — bez tej synchronizacji pole
  // zostawało puste, a „Zapisz zmiany" wysłałoby null i skasowało zapisaną nazwę.
  const [syncedDisplayName, setSyncedDisplayName] = useState(loadedDisplayName);
  if (syncedDisplayName !== loadedDisplayName) {
    setSyncedDisplayName(loadedDisplayName);
    setProfileDisplayName(loadedDisplayName);
  }
  const [profileLanguage, setProfileLanguage] = useState<RecordLanguage>(
    (i18n.language as RecordLanguage) === 'en' ? 'en' : 'pl',
  );
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  // Własne nazwy języków profilu (literał `t()` per kod — widoczny dla skanera `check:i18n`);
  // `satisfies` pilnuje, żeby mapa pokrywała każdy kod z `RECORD_LANGUAGES`.
  const profileLanguageLabels = {
    pl: t('tenant.profile.profileForm.languagePl'),
    en: t('tenant.profile.profileForm.languageEn'),
  } satisfies Record<RecordLanguage, string>;

  // Avatar state.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarHasUploaded, setAvatarHasUploaded] = useState(false);

  // Wczytanie zapisanego avatara po wejściu na stronę (strumień z API przez Bearer — nie ma
  // publicznego URL-a). 404 = brak avatara → zostaje placeholder z inicjałem.
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    api
      .get<Blob>('/auth/profile/avatar', { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setAvatarPreview(objectUrl);
        setAvatarHasUploaded(true);
      })
      .catch(() => {
        /* brak avatara albo błąd odczytu — placeholder */
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  if (loading && !me) {
    return (
      <ProfileShell embedded={embedded}>
        <div className="mx-auto flex max-w-3xl items-center justify-center py-20">
          <Spinner />
        </div>
      </ProfileShell>
    );
  }

  if (!me) {
    return (
      <ProfileShell embedded={embedded}>
        <div className="mx-auto max-w-3xl py-20 text-center text-text-tertiary">
          {t('tenant.profile.notLoaded')}
        </div>
      </ProfileShell>
    );
  }

  const handleLogoutAllConfirm = async () => {
    setLogoutSubmitting(true);
    try {
      await logoutAll();
    } finally {
      setLogoutSubmitting(false);
      setLogoutModalOpen(false);
      navigate('/login', { replace: true });
    }
  };

  const handleProfileSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setProfileSubmitting(true);
    try {
      const trimmed = profileDisplayName.trim();
      const body: { displayName?: string | null; language: RecordLanguage } = {
        language: profileLanguage,
      };
      // Pusty string -> null (clear). Niepusty -> nowa wartosc.
      body.displayName = trimmed === '' ? null : trimmed;
      await api.put('/auth/profile', body);
      await i18n.changeLanguage(profileLanguage);
      await refreshMe();
      toast.show({
        variant: 'success',
        title: t('tenant.profile.profileForm.successTitle'),
      });
    } catch {
      toast.show({
        variant: 'error',
        title: t('tenant.profile.profileForm.errorTitle'),
        description: t('tenant.profile.profileForm.errorDescription'),
      });
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset wartosci inputa zeby ten sam plik mogl byc wybrany ponownie po error.
    e.target.value = '';

    if (!AVATAR_ALLOWED_MIME.has(file.type)) {
      toast.show({
        variant: 'error',
        title: t('tenant.profile.avatar.errors.unsupportedTitle'),
        description: t('tenant.profile.avatar.errors.unsupportedDescription'),
      });
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.show({
        variant: 'error',
        title: t('tenant.profile.avatar.errors.tooLargeTitle'),
        description: t('tenant.profile.avatar.errors.tooLargeDescription'),
      });
      return;
    }

    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/auth/profile/avatar', formData);

      // Lokalny podglad — backend nie zwraca pelnego URL-a, wiec uzywamy FileReader.
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          setAvatarPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
      setAvatarHasUploaded(true);

      toast.show({
        variant: 'success',
        title: t('tenant.profile.avatar.successTitle'),
      });
    } catch {
      toast.show({
        variant: 'error',
        title: t('tenant.profile.avatar.errors.uploadFailedTitle'),
        description: t('tenant.profile.avatar.errors.uploadFailedDescription'),
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarDelete = async () => {
    setAvatarUploading(true);
    try {
      await api.delete('/auth/profile/avatar');
      setAvatarPreview(null);
      setAvatarHasUploaded(false);
      toast.show({
        variant: 'success',
        title: t('tenant.profile.avatar.deletedTitle'),
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        // Avatar nie ustawiony — synchronizujemy lokalny stan.
        setAvatarPreview(null);
        setAvatarHasUploaded(false);
      } else {
        toast.show({
          variant: 'error',
          title: t('tenant.profile.avatar.errors.deleteFailedTitle'),
        });
      }
    } finally {
      setAvatarUploading(false);
    }
  };

  const totpEnabled = me.user.totpEnabled;
  const avatarInitial = (me.user.displayName ?? me.user.email).charAt(0).toUpperCase();

  return (
    <ProfileShell embedded={embedded}>
      <div
        className={
          // Osadzony w Ustawieniach (wg Figmy) = PEŁNA szerokość treści; samodzielna strona
          // /profile = wycentrowana kolumna max-w-3xl (klasyczny profil).
          embedded ? 'flex w-full flex-col gap-6' : 'mx-auto flex max-w-3xl flex-col gap-6'
        }
      >
        <header>
          <h1 className="text-2xl font-semibold tracking-tight text-text-primary">
            {t('tenant.profile.title')}
          </h1>
          <p className="mt-1 text-sm text-text-tertiary">{t('tenant.profile.subtitle')}</p>
        </header>

        {/* Card: Dane konta */}
        <Card>
          <h2 className="-mx-5 mb-4 border-b border-border-secondary px-5 pb-3 text-lg font-semibold text-text-primary">
            {t('tenant.profile.account.heading')}
          </h2>
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-2xs font-semibold uppercase tracking-[0.06em] text-text-muted">
                {t('tenant.profile.account.email')}
              </dt>
              <dd className="mt-1 text-sm text-text-primary">{me.user.email}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase tracking-[0.06em] text-text-muted">
                {t('tenant.profile.account.displayName')}
              </dt>
              <dd className="mt-1 text-sm text-text-primary">
                {me.user.displayName ?? t('tenant.profile.account.displayNameEmpty')}
              </dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase tracking-[0.06em] text-text-muted">
                {t('tenant.profile.account.tenant')}
              </dt>
              <dd className="mt-1 text-sm text-text-primary">{me.tenant.name}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase tracking-[0.06em] text-text-muted">
                {t('tenant.profile.account.role')}
              </dt>
              <dd className="mt-1">
                {me.user.isSuperUser ? (
                  // Czerwony tag z obwódką — 1:1 z Figmą i z odznaką super-usera na liście
                  // użytkowników (spójność w całej apce).
                  <Badge
                    color="red"
                    className="border border-red-300 bg-red-50 text-red-600 dark:border-red-800"
                  >
                    {t('tenant.profile.account.roleSuper')}
                  </Badge>
                ) : (
                  <Badge color="gray">{t('tenant.profile.account.roleUser')}</Badge>
                )}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Card: Profil i jezyk (T10.11) */}
        <Card>
          <h2 className="-mx-5 mb-4 border-b border-border-secondary px-5 pb-3 text-lg font-semibold text-text-primary">
            {t('tenant.profile.profileForm.heading')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('tenant.profile.profileForm.description')}
          </p>
          <form
            onSubmit={handleProfileSubmit}
            className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
            data-testid="profile-form"
          >
            <Input
              label={t('tenant.profile.profileForm.displayNameLabel')}
              type="text"
              value={profileDisplayName}
              onChange={(e) => setProfileDisplayName(e.target.value)}
              disabled={profileSubmitting}
              maxLength={150}
              data-testid="profile-display-name"
            />
            <div className="flex flex-col gap-1">
              <label htmlFor="profile-language" className="text-sm font-medium text-text-secondary">
                {t('tenant.profile.profileForm.languageLabel')}
              </label>
              <Dropdown
                id="profile-language"
                value={profileLanguage}
                onChange={(v) => setProfileLanguage(v === 'en' ? 'en' : 'pl')}
                disabled={profileSubmitting}
                data-testid="profile-language"
                className="h-10 rounded-lg border border-border-secondary bg-bg-elevated px-3 text-sm text-text-primary"
                options={RECORD_LANGUAGES.map((code) => ({
                  value: code,
                  label: profileLanguageLabels[code],
                }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                loading={profileSubmitting}
                data-testid="profile-form-submit"
                className="rounded-full"
              >
                {profileSubmitting
                  ? t('tenant.profile.profileForm.submitting')
                  : t('tenant.profile.profileForm.submit')}
              </Button>
            </div>
          </form>
        </Card>

        {/* Card: Avatar (T10.11) */}
        <Card>
          <h2 className="-mx-5 mb-4 border-b border-border-secondary px-5 pb-3 text-lg font-semibold text-text-primary">
            {t('tenant.profile.avatar.heading')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('tenant.profile.avatar.description')}
          </p>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div
              className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border-secondary bg-bg-tertiary"
              data-testid="avatar-preview"
            >
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt={t('tenant.profile.avatar.alt')}
                  className="h-full w-full object-cover"
                  data-testid="avatar-preview-img"
                />
              ) : (
                <span
                  className="text-3xl font-bold text-text-muted"
                  data-testid="avatar-placeholder"
                >
                  {avatarInitial}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => void handleAvatarChange(e)}
                className="hidden"
                data-testid="avatar-file-input"
              />
              <Button
                variant="secondary"
                onClick={handleAvatarPick}
                loading={avatarUploading}
                disabled={avatarUploading}
                data-testid="avatar-upload-cta"
                className="rounded-full"
              >
                {t('tenant.profile.avatar.uploadCta')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => void handleAvatarDelete()}
                disabled={avatarUploading || !avatarHasUploaded}
                data-testid="avatar-delete-cta"
                className="rounded-full"
              >
                {t('tenant.profile.avatar.deleteCta')}
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs text-text-muted">{t('tenant.profile.avatar.hint')}</p>
        </Card>

        {/* Card: Bezpieczeństwo */}
        <Card>
          <h2 className="-mx-5 mb-4 border-b border-border-secondary px-5 pb-3 text-lg font-semibold text-text-primary">
            {t('tenant.profile.security.heading')}
          </h2>

          <div
            className="mt-4 flex flex-col gap-3 rounded-lg border border-border-secondary bg-bg-tertiary p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid="totp-section"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-text-primary">
                {t('tenant.profile.security.totpLabel')}
              </span>
              {totpEnabled ? (
                <Badge color="green" data-testid="totp-badge-on">
                  {t('tenant.profile.security.totpOn')}
                </Badge>
              ) : (
                <Badge color="gray" data-testid="totp-badge-off">
                  {t('tenant.profile.security.totpOff')}
                </Badge>
              )}
            </div>
            {totpEnabled ? (
              <Button
                variant="secondary"
                onClick={() => setDisableModalOpen(true)}
                className="rounded-full"
              >
                {t('tenant.profile.security.disableCta')}
              </Button>
            ) : (
              <Link to="/profile/2fa/setup">
                <Button variant="secondary" className="rounded-full">
                  {t('tenant.profile.security.setupCta')}
                </Button>
              </Link>
            )}
          </div>

          <div className="mt-6">
            <h3 className="text-sm font-semibold text-text-primary">
              {t('tenant.profile.password.heading')}
            </h3>
            <p className="mt-1 text-xs text-text-tertiary">
              {t('tenant.profile.password.description')}
            </p>
            <div className="mt-4">
              <ChangePasswordForm onSuccess={() => void refreshMe()} />
            </div>
          </div>
        </Card>

        {/* Card: Sesje */}
        <Card>
          <h2 className="-mx-5 mb-4 border-b border-border-secondary px-5 pb-3 text-lg font-semibold text-text-primary">
            {t('tenant.profile.sessions.heading')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('tenant.profile.sessions.description')}
          </p>
          <div className="mt-4">
            <Button
              variant="secondary"
              onClick={() => setLogoutModalOpen(true)}
              className="rounded-full"
            >
              {t('tenant.profile.sessions.logoutAllCta')}
            </Button>
          </div>
        </Card>
      </div>

      <Modal
        open={logoutModalOpen}
        onClose={() => (logoutSubmitting ? undefined : setLogoutModalOpen(false))}
        title={t('tenant.profile.sessions.modalTitle')}
        description={t('tenant.profile.sessions.modalDescription')}
        closeOnBackdrop={!logoutSubmitting}
        closeOnEsc={!logoutSubmitting}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setLogoutModalOpen(false)}
              disabled={logoutSubmitting}
            >
              {t('tenant.profile.sessions.modalCancel')}
            </Button>
            <Button variant="danger" loading={logoutSubmitting} onClick={handleLogoutAllConfirm}>
              {t('tenant.profile.sessions.modalConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-tertiary">{t('tenant.profile.sessions.modalBody')}</p>
      </Modal>

      <Disable2FAModal
        open={disableModalOpen}
        onClose={() => setDisableModalOpen(false)}
        onSuccess={async () => {
          setDisableModalOpen(false);
          await refreshMe();
          toast.show({ variant: 'success', title: t('tenant.2fa.toastDisabled') });
        }}
      />
    </ProfileShell>
  );
}
